import React, { useState, useEffect, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import sellerAnalyticsService from '../services/sellerAnalyticsService';
import { SellerSpinner } from '../components/skeleton';
import './SellerAnalytics.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const SellerAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('7d');
  const [groupBy, setGroupBy] = useState('day');
  const [category, setCategory] = useState('');
  const [source, setSource] = useState('');
  const [device, setDevice] = useState('');
  const [compareWithPrevious, setCompareWithPrevious] = useState(false);

  // Opzioni dinamiche per i filtri
  const categoryOptions = useMemo(() => {
    if (!analytics || !analytics.category_stats) return [];
    return analytics.category_stats.map(c => c.product_category).filter(Boolean);
  }, [analytics]);
  const sourceOptions = useMemo(() => {
    if (!analytics || !analytics.source_stats) return [];
    return analytics.source_stats.map(s => s.impression_source).filter(Boolean);
  }, [analytics]);
  const deviceOptions = useMemo(() => {
    if (!analytics || !analytics.source_stats) return ['desktop', 'mobile', 'tablet'];
    // In futuro: estrarre da analytics se disponibile
    return ['desktop', 'mobile', 'tablet'];
  }, [analytics]);

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  const lineChartOptions = {
    ...chartOptions,
    plugins: {
      ...chartOptions.plugins,
      title: {
        display: true,
        text: 'Performance Over Time',
      },
    },
  };



  const doughnutChartOptions = {
    ...chartOptions,
    plugins: {
      ...chartOptions.plugins,
      title: {
        display: true,
        text: 'Event Distribution',
      },
    },
  };

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line
  }, [dateRange, groupBy, category, source, device, compareWithPrevious]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await sellerAnalyticsService.getSellerStats({
        dateRange,
        groupBy,
        category: category || undefined,
        source: source || undefined,
        device: device || undefined,
        compareWithPrevious
      });
      if (response && response.success && response.data) {
        setAnalytics(response.data);
      } else {
        throw new Error('Invalid response format from analytics API');
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return num.toLocaleString();
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '€0.00';
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const getMetricCard = (title, value, subtitle, color = 'blue') => (
    <div className={`metric-card ${color}`}>
      <h3>{title}</h3>
      <div className="metric-value">{value}</div>
      {subtitle && <div className="metric-subtitle">{subtitle}</div>}
    </div>
  );

  // Funzione per badge confronto
  const getDeltaBadge = (current, previous) => {
    if (previous === null || previous === undefined) return null;
    const prev = Number(previous) || 0;
    const curr = Number(current) || 0;
    if (prev === 0 && curr === 0) return null;
    if (prev === 0 && curr > 0) return <span className="delta-badge up">+100%</span>;
    const delta = ((curr - prev) / Math.max(prev, 1)) * 100;
    const sign = delta > 0 ? '+' : '';
    const cls = delta > 0 ? 'delta-badge up' : 'delta-badge down';
    return <span className={cls}>{sign}{delta.toFixed(1)}%</span>;
  };

  if (loading) {
    return <SellerSpinner text="Caricamento analytics..." />;
  }

  if (error) {
    return (
      <>
        <div className="seller-page-header">
          <h1>Analytics</h1>
          <p>Analytics del venditore</p>
        </div>
        <div className="seller-message error">
          <h3>Errore nel caricamento</h3>
          <p>{error}</p>
          <button onClick={fetchAnalytics} className="seller-btn seller-btn-primary">
            Riprova
          </button>
        </div>
      </>
    );
  }

  if (!analytics) {
    return (
      <>
        <div className="seller-page-header">
          <h1>Analytics</h1>
          <p>Analytics del venditore</p>
        </div>
        <div className="seller-message">
          <p>Nessun dato analytics disponibile</p>
        </div>
      </>
    );
  }

  // Prepare chart data
  const timeSeriesData = {
    labels: analytics.time_series?.map(item => item.date) || [],
    datasets: [
      {
        label: 'Impression Card',
        data: analytics.time_series?.map(item => item.views) || [],
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        fill: true,
      },
      {
        label: 'Click su Dettagli',
        data: analytics.time_series?.map(item => item.clicks) || [],
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        fill: true,
      },
      {
        label: 'Aggiunte Wishlist',
        data: analytics.time_series?.map(item => item.wishlist_adds) || [],
        borderColor: 'rgb(54, 162, 235)',
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        fill: true,
      }
    ]
  };



  const eventDistributionData = {
    labels: ['Impression Card', 'Click su Dettagli', 'Aggiunte Wishlist', 'Click verso il tuo sito'],
    datasets: [
      {
        data: [
          analytics.total_views || 0,
          analytics.total_clicks || 0,
          analytics.total_wishlist_adds || 0,
          analytics.total_external_clicks || 0
        ],
        backgroundColor: [
          'rgba(75, 192, 192, 0.8)',
          'rgba(255, 99, 132, 0.8)',
          'rgba(54, 162, 235, 0.8)',
          'rgba(255, 205, 86, 0.8)'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }
    ]
  };

  return (
    <>
      <div className="seller-page-header">
        <h1>Analytics</h1>
        <p>Analytics del venditore</p>
      </div>

      <div className="analytics-filters">
            <div className="filter-group">
              <label htmlFor="dateRange">Periodo:</label>
              <select id="dateRange" value={dateRange} onChange={e => setDateRange(e.target.value)}>
                <option value="1d">Ultimo giorno</option>
                <option value="7d">Ultima settimana</option>
                <option value="30d">Ultimo mese</option>
                <option value="90d">Ultimi 3 mesi</option>
              </select>
            </div>
            <div className="filter-group">
              <label htmlFor="groupBy">Raggruppa per:</label>
              <select id="groupBy" value={groupBy} onChange={e => setGroupBy(e.target.value)}>
                <option value="hour">Ora</option>
                <option value="day">Giorno</option>
                <option value="week">Settimana</option>
                <option value="month">Mese</option>
              </select>
            </div>
            <div className="filter-group">
              <label htmlFor="category">Categoria:</label>
              <select id="category" value={category} onChange={e => setCategory(e.target.value)}>
                <option value="">Tutte</option>
                {categoryOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
            <div className="filter-group">
              <label htmlFor="source">Fonte:</label>
              <select id="source" value={source} onChange={e => setSource(e.target.value)}>
                <option value="">Tutte</option>
                {sourceOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
            <div className="filter-group">
              <label htmlFor="device">Dispositivo:</label>
              <select id="device" value={device} onChange={e => setDevice(e.target.value)}>
                <option value="">Tutti</option>
                {deviceOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
            <div className="filter-group" style={{marginTop: 22}}>
              <label htmlFor="compareWithPrevious">
                <input
                  type="checkbox"
                  id="compareWithPrevious"
                  checked={compareWithPrevious}
                  onChange={e => setCompareWithPrevious(e.target.checked)}
                  style={{marginRight: 6}}
                />
                Confronta con periodo precedente
              </label>
            </div>
          </div>

        {/* Grafico Performance nel Tempo - Spostato prima delle metriche */}
        <div className="charts-section">
          <div className="chart-container">
            <h3>Performance nel Tempo</h3>
            <div className="chart-wrapper">
              {analytics.time_series && analytics.time_series.length > 0 ? (
                <Line data={timeSeriesData} options={lineChartOptions} />
              ) : (
                <div className="no-data-chart">
                  <p>Nessun dato temporale disponibile</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="metrics-grid">
          {/* Metriche Base */}
          {getMetricCard(
            'Impression Totali',
            <>{formatNumber(analytics.total_views)} {getDeltaBadge(analytics.total_views, analytics.previous_period?.total_views)}</>,
            'Visualizzazioni delle product card',
            'blue'
          )}
          {getMetricCard(
            'Click su Dettagli',
            <>{formatNumber(analytics.total_clicks)} {getDeltaBadge(analytics.total_clicks, analytics.previous_period?.total_clicks)}</>,
            'Click da card a pagina dettagli prodotto',
            'green'
          )}
          {getMetricCard(
            'Aggiunte Wishlist',
            <>{formatNumber(analytics.total_wishlist_adds)} {getDeltaBadge(analytics.total_wishlist_adds, analytics.previous_period?.total_wishlist_adds)}</>,
            'Prodotti aggiunti ai preferiti',
            'purple'
          )}
          {getMetricCard(
            'Click verso il tuo sito',
            <>{formatNumber(analytics.total_external_clicks)} {getDeltaBadge(analytics.total_external_clicks, analytics.previous_period?.total_external_clicks)}</>,
            'Click verso le tue pagine prodotto sul tuo sito',
            'orange'
          )}
          
          {/* Metriche Avanzate */}
          {getMetricCard(
            'CTR Interno',
            <>{analytics.ctr_percentage || 0}% {getDeltaBadge(analytics.ctr_percentage, analytics.previous_period ? (analytics.previous_period.total_views > 0 ? (analytics.previous_period.total_clicks / analytics.previous_period.total_views * 100) : 0) : null)}</>,
            'Click su dettagli / Impression card',
            'red'
          )}
          {getMetricCard(
            'CTR Esterno',
            <>{analytics.external_click_rate_percentage || 0}% {getDeltaBadge(analytics.external_click_rate_percentage, analytics.previous_period ? (analytics.previous_period.total_views > 0 ? (analytics.previous_period.total_external_clicks / analytics.previous_period.total_views * 100) : 0) : null)}</>,
            'Click verso il tuo sito / Impression card',
            'indigo'
          )}
          {getMetricCard(
            'Tasso Wishlist',
            <>{analytics.wishlist_rate_percentage || 0}% {getDeltaBadge(analytics.wishlist_rate_percentage, analytics.previous_period ? (analytics.previous_period.total_views > 0 ? (analytics.previous_period.total_wishlist_adds / analytics.previous_period.total_views * 100) : 0) : null)}</>,
            'Wishlist / Impression card',
            'teal'
          )}
          {getMetricCard(
            'Fonte Migliore',
            analytics.insights?.best_performing_source ? 
              analytics.insights.best_performing_source.impression_source : 
              'N/A',
            'Fonte con CTR più alto',
            'pink'
          )}
        </div>

        <div className="charts-section">
          <div className="chart-container">
            <h3>Distribuzione Eventi</h3>
            <div className="chart-wrapper">
              <Doughnut data={eventDistributionData} options={doughnutChartOptions} />
            </div>
          </div>
        </div>

        {/* Sezione Statistiche per Categoria */}
        {analytics.category_stats && analytics.category_stats.length > 0 && (
          <div className="category-stats-section">
            <h3>Performance per Categoria</h3>
            <div className="stats-grid">
              {analytics.category_stats.slice(0, 6).map((category, index) => (
                <div key={index} className="category-stat-card">
                  <h4>{category.product_category}</h4>
                  <div className="stat-metrics">
                    <div className="stat-item">
                      <span className="stat-label">Visualizzazioni:</span>
                      <span className="stat-value">{formatNumber(category.views)}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Clic:</span>
                      <span className="stat-value">{formatNumber(category.clicks)}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">CTR:</span>
                      <span className="stat-value">{category.ctr}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sezione Statistiche per Fonte */}
        {analytics.source_stats && analytics.source_stats.length > 0 && (
          <div className="source-stats-section">
            <h3>Performance per Fonte di Traffico</h3>
            <div className="stats-grid">
              {analytics.source_stats.slice(0, 6).map((source, index) => (
                <div key={index} className="source-stat-card">
                  <h4>{source.impression_source}</h4>
                  <div className="stat-metrics">
                    <div className="stat-item">
                      <span className="stat-label">Visualizzazioni:</span>
                      <span className="stat-value">{formatNumber(source.views)}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Clic:</span>
                      <span className="stat-value">{formatNumber(source.clicks)}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">CTR:</span>
                      <span className="stat-value">{source.ctr}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sezione Insights */}
        {analytics.insights && (
          <div className="insights-section">
            <h3>Insights e Raccomandazioni</h3>
            <div className="insights-grid">
              {analytics.insights.best_performing_category && (
                <div className="insight-card positive">
                  <h4>Categoria Migliore</h4>
                  <p><strong>{analytics.insights.best_performing_category.product_category}</strong></p>
                  <p>CTR: {analytics.insights.best_performing_category.ctr}%</p>
                  <p>Visualizzazioni: {formatNumber(analytics.insights.best_performing_category.views)}</p>
                </div>
              )}
              {analytics.insights.best_performing_source && (
                <div className="insight-card positive">
                  <h4>Fonte Migliore</h4>
                  <p><strong>{analytics.insights.best_performing_source.impression_source}</strong></p>
                  <p>CTR: {analytics.insights.best_performing_source.ctr}%</p>
                  <p>Visualizzazioni: {formatNumber(analytics.insights.best_performing_source.views)}</p>
                </div>
              )}
              {analytics.insights.top_product && (
                <div className="insight-card positive">
                  <h4>Prodotto Top</h4>
                  <p><strong>{analytics.insights.top_product.name}</strong></p>
                  <p>Visualizzazioni: {formatNumber(analytics.insights.top_product.views)}</p>
                  <p>Clic: {formatNumber(analytics.insights.top_product.clicks)}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {analytics.top_products && analytics.top_products.length > 0 && (
          <div className="products-table-section">
            <h3>Top Prodotti per Performance</h3>
            <div className="table-container">
              <table className="products-table">
                <thead>
                  <tr>
                    <th>Prodotto</th>
                    <th>Impression Card</th>
                    <th>Click su Dettagli</th>
                    <th>Wishlist</th>
                    <th>CTR Interno</th>
                    <th>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.top_products.slice(0, 10).map((product, index) => (
                    <tr key={index}>
                      <td>{product.name}</td>
                      <td>{formatNumber(product.views)}</td>
                      <td>{formatNumber(product.clicks)}</td>
                      <td>{formatNumber(product.wishlist_adds)}</td>
                      <td>
                        {((product.clicks / Math.max(product.views, 1)) * 100).toFixed(2)}%
                      </td>
                      <td>
                        <a 
                          href={`/products/${product.product_id}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="view-product-btn"
                        >
                          Visualizza
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
    </>
  );
};

export default SellerAnalytics; 